import { CoordinatorService } from './coordinator.service';
import { Keyword } from './keyword';
import { ParserFile } from './parser-file';
import { ScriptInput } from './script-input';
import { ScriptKeyword } from './script-keyword';
import { ScriptOutput } from './script-output';
import { ScriptItem } from './script-x-item';
import { XItem } from './x-item';

export class ScriptsSystem$ {
    #coords: CoordinatorService;

    constructor(coords: CoordinatorService) {
        this.#coords = coords;
    }

    async exportProject() {
        if (!this.#coords.acl$.isOn('project', 'export')) return;

        const out = this.#coords.script$.output(true);
        const $project = this.#coords.project$.current!.scriptClass;

        out.addProject($project);
        out.addDataset(this.#coords.dataset$.main.scriptClass);
        out.addSubsets($project.subsets);
        out.addKeywords($project.keywords);
        out.addDatabins($project.databins);
        out.addComments($project.comments);
        out.addAttachments($project.attachments);
        out.addRatings($project.ratings);
        out.addTags($project.tags);
        out.addAutousers(this.#coords.script$.project!.userManager.othersAsAutouserList());
        out.addAutousers(this.#coords.script$.project!.autouserManager.list());
        out.save();
    }

    async parseFile(pf: ParserFile) {
        const size = this.#coords.utils$.bytesToHumanReadable(pf.localFile?.size);
        const inp = new ScriptInput(this.#coords);
        const payload = (await inp.process(pf.localFile!)) as any;

        pf.connection?.refreshMessageWidget(
            'info',
            `${this.#coords.text('msg_parsing')} ${pf.localFile?.name} [${size}]`,
        );

        if (payload.ns === 'https://memowl.app') {
            const items = payload.payload?.data.items || payload.data?.items;
            pf.suggestName = pf.localFile?.name || '';
            pf.warningMessage = payload.payload?.warning || '';
            items && pf.submitXItems(items.map((itm: any) => new XItem(this.#coords, itm)));
        } else {
            this.#coords.worker$.parser.directPost({
                action: 'load-file',
                payload: {
                    parserFileId: pf.id,
                    file: pf.localFile,
                },
            });
        }
    }

    async importItems(itmLs: any[], isReplace = false) {
        const inp = new ScriptInput(this.#coords);
        const payload = (await inp.readFile()) as any;
        let xitemLs: XItem[];

        if (payload.ns === 'https://memowl.app') {
            xitemLs = payload.data.items.map((itm: any) => new XItem(this.#coords, itm));
        } else return;

        this.#coords.display$.markForCheck();
    }

    #out(itmLs: ScriptItem[] | XItem[] | true) {
        if (itmLs === true) return new ScriptOutput(this.#coords);
        if (!itmLs.length) return;
        if (itmLs[0] instanceof XItem)
            itmLs = itmLs.map((itm) => new ScriptItem(this.#coords, itm as XItem));
        const out = new ScriptOutput(this.#coords);
        out.addItems(itmLs as ScriptItem[]);
        return out;
    }

    async exportRIS(itmLs: ScriptItem[] | XItem[]) {
        await this.#out(itmLs)?.ris();
    }

    async exportDOI(itmLs: ScriptItem[] | XItem[]) {
        await this.#out(itmLs)?.doi();
    }

    async exportCSV(itmLs: ScriptItem[] | XItem[]) {
        await this.#out(itmLs)?.csv();
    }

    async exportCSVTeam(payload: any) {
        await this.#out(true)?.csv({ isTeamRT: true, payload });
    }

    async exportItems(itmLs: ScriptItem[] | XItem[]) {
        if (!itmLs.length) return;
        if (itmLs[0] instanceof XItem)
            itmLs = itmLs.map((itm) => new ScriptItem(this.#coords, itm as XItem));
        const out = new ScriptOutput(this.#coords);
        out.addItems(itmLs as ScriptItem[]);
        await out.save({ isZipped: true, title: 'items' });
    }

    async exportKeywords(kwLs: Keyword[] | ScriptKeyword[]) {
        if (!kwLs.length) return;
        if (kwLs[0] instanceof Keyword) {
            kwLs = kwLs.map((kw) => new ScriptKeyword(this.#coords, kw as Keyword));
        }
        const out = new ScriptOutput(this.#coords);
        out.addKeywords(kwLs as ScriptKeyword[]);
        await out.save({ isZipped: true, title: 'keywords' });
    }

    async importKeywords(isReplace = false) {
        const inp = new ScriptInput(this.#coords);
        const payload = (await inp.readFile()) as any;
        let kwLs: Keyword[];
        if (typeof payload === 'string') {
            const ls = payload
                .split('\n')
                .map((x) => x.trim())
                .filter((x) => x);
            kwLs = ls.map((x) => {
                let w: number;
                let i = x.indexOf('::');
                if (i === -1) w = 1;
                else {
                    w = parseInt(x.substring(i + 2).trim());
                    x = x.substring(0, i).trim();
                }
                return new Keyword(this.#coords, { regexp: x, weight: w });
            });
        } else if (payload.ns === 'https://memowl.app') {
            kwLs = payload.data.keywords.map((kw: any) => new Keyword(this.#coords, kw));
        } else return;

        this.#coords.keyword$.addKeywords(kwLs, isReplace);
        this.#coords.display$.markForCheck();
    }
}
